/**
 * Objective types read hook.
 *
 * Idea objective types (commercial / awareness / informative / ...) are a
 * small server-defined enum. This module exposes a read-only react-query
 * hook over GET ``/api/objective-types``. The pure normalisation and
 * label-resolution helpers live in ``objectiveTypesHelpers`` so they stay
 * testable without the HTTP / alias chain.
 *
 * Contract: the endpoint may return either a bare array or an envelope
 * ``{ objectiveTypes: [...] }``. Each entry is ``{ id, key, label }``. The
 * feature is additive and the endpoint may not exist yet, so any failure
 * (404 included) resolves to an empty list with retries disabled. Callers
 * fall back to the raw stored key when no matching label is found.
 */

import { useQuery } from "@tanstack/react-query";
import { useHttpClient } from "@/core/http/HttpClientContext";
import {
  ObjectiveType,
  ObjectiveTypesResponse,
  normalizeObjectiveTypes,
  resolveObjectiveLabel,
} from "./objectiveTypesHelpers";

export type { ObjectiveType };
export { normalizeObjectiveTypes, resolveObjectiveLabel };

export const objectiveTypeKeys = {
  all: ["objective-types"] as const,
};

/**
 * Fetch the available objective types. Resolves to ``[]`` on any error so
 * the create form and idea surfaces degrade to a no-objective state rather
 * than throwing.
 */
export function useObjectiveTypes() {
  const http = useHttpClient();
  return useQuery({
    queryKey: objectiveTypeKeys.all,
    queryFn: async (): Promise<ObjectiveType[]> => {
      try {
        const data = await http.get<ObjectiveTypesResponse>("/api/objective-types");
        return normalizeObjectiveTypes(data);
      } catch {
        return [];
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}
